/**
 * Thin wrappers over the generated SDK.
 *
 * Each one unwraps `{ data }` and returns the response body, so the query hooks
 * in `./queries.ts` stay free of SDK plumbing. The client is configured with
 * `throwOnError: true` (see `src/lib/api/client.ts`), so a non-2xx rejects with
 * `{ status, body }` and never reaches these return statements.
 *
 * Nothing here touches React.
 */

import {
  addTicketComment as sdkAddTicketComment,
  answerTicketInfoRequest as sdkAnswerTicketInfoRequest,
  closeTicket as sdkCloseTicket,
  confirmTicketResolution as sdkConfirmTicketResolution,
  createTicket as sdkCreateTicket,
  findSimilarTickets as sdkFindSimilarTickets,
  getIncidentFollowups as sdkGetIncidentFollowups,
  getRequestOffering as sdkGetRequestOffering,
  getTicket as sdkGetTicket,
  getTicketAttachmentDownload as sdkGetTicketAttachmentDownload,
  getTicketByNumber as sdkGetTicketByNumber,
  getTicketMetadata as sdkGetTicketMetadata,
  joinSimilarTicket as sdkJoinSimilarTicket,
  listNamedOnTickets as sdkListNamedOnTickets,
  listRequestOfferings as sdkListRequestOfferings,
  listRoleLists as sdkListRoleLists,
  listTickets as sdkListTickets,
  listTicketTimeline as sdkListTicketTimeline,
  reopenTicket as sdkReopenTicket,
} from "@/lib/api";

import type {
  IncidentFollowupsRequest,
  IncidentFollowupsResponse,
  ListRequestOfferingsQuery,
  ListTicketsQuery,
  ListTicketTimelineQuery,
  NamedOnTicketListResponse,
  RequestOfferingDetail,
  RequestOfferingListResponse,
  RoleListsResponse,
  SimilarTicketJoinRequest,
  SimilarTicketJoinResponse,
  SimilarTicketsRequest,
  SimilarTicketsResponse,
  TicketAttachmentDownload,
  TicketCloseRequest,
  TicketCommentCreateRequest,
  TicketCreateRequest,
  TicketDetail,
  TicketInfoAnswerRequest,
  TicketListResponse,
  TicketMetadataResponse,
  TicketReopenRequest,
  TicketResolutionConfirmRequest,
  TicketTimelineEntry,
  TicketTimelineResponse,
} from "./contract";

/**
 * A fresh `Idempotency-Key` for a state-changing POST.
 *
 * The contract requires the header on `createTicket`; the value is an opaque
 * client string, and one UUID per submit *attempt* is exactly the intended
 * semantics — a retry of the same attempt reuses it, a new attempt does not.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

// ─── Reads ───────────────────────────────────────────────────────────────

/** The signed-in user's own tickets. The requester filter is server-side. */
export async function listTickets(query: ListTicketsQuery): Promise<TicketListResponse> {
  const { data } = await sdkListTickets({ query, throwOnError: true });
  return data;
}

/**
 * Requests someone else raised that name the signed-in user in a person field
 * (e.g. as a new starter's manager). Scoped to the session server-side (2 Oct).
 */
export async function listNamedOnTickets(translateTo?: string): Promise<NamedOnTicketListResponse> {
  const { data } = await sdkListNamedOnTickets({
    query: { translate_to: translateTo ?? null },
    throwOnError: true,
  });
  return data;
}

export async function getTicket(ticketId: string, translateTo?: string): Promise<TicketDetail> {
  const { data } = await sdkGetTicket({
    path: { ticket_id: ticketId },
    query: { translate_to: translateTo ?? null },
    throwOnError: true,
  });
  return data;
}

/** Deep links out of Ivanti notification email arrive by number, not id. */
export async function getTicketByNumber(
  ticketNumber: string,
  translateTo?: string,
): Promise<TicketDetail> {
  const { data } = await sdkGetTicketByNumber({
    path: { ticket_number: ticketNumber },
    query: { translate_to: translateTo ?? null },
    throwOnError: true,
  });
  return data;
}

export async function listTicketTimeline(
  ticketId: string,
  query: ListTicketTimelineQuery = {},
): Promise<TicketTimelineResponse> {
  const { data } = await sdkListTicketTimeline({
    path: { ticket_id: ticketId },
    query,
    throwOnError: true,
  });
  return data;
}

/** Tenant-resolved pick lists (categories, cause codes, attachment limits). */
export async function getTicketMetadata(): Promise<TicketMetadataResponse> {
  const { data } = await sdkGetTicketMetadata({ throwOnError: true });
  return data;
}

export async function getTicketAttachmentDownload(
  ticketId: string,
  attachmentId: string,
): Promise<TicketAttachmentDownload> {
  const { data } = await sdkGetTicketAttachmentDownload({
    path: { ticket_id: ticketId, attachment_id: attachmentId },
    throwOnError: true,
  });
  return data;
}

export async function listRequestOfferings(
  query: ListRequestOfferingsQuery = {},
): Promise<RequestOfferingListResponse> {
  const { data } = await sdkListRequestOfferings({ query, throwOnError: true });
  return data;
}

export async function getRequestOffering(
  offeringId: string,
  translateTo?: string,
): Promise<RequestOfferingDetail> {
  const { data } = await sdkGetRequestOffering({
    path: { offering_id: offeringId },
    query: { translate_to: translateTo ?? null },
    throwOnError: true,
  });
  return data;
}

/** The role pickers of the Master approval matrix (8 Oct): the Bluestar areas for "Roles needed". */
export async function listRoleLists(): Promise<RoleListsResponse> {
  const { data } = await sdkListRoleLists({ throwOnError: true });
  return data;
}

// ─── Writes ──────────────────────────────────────────────────────────────

/**
 * Raises a ticket.
 *
 * `Idempotency-Key` is required by the contract (400 without it). The caller
 * passes the key it generated for this submit attempt so a user-driven retry of
 * the same attempt cannot create a second ticket. `signal` lets a caller give
 * up waiting (the emergency page's client timeout); a retry with the same key
 * still replays rather than duplicates.
 */
export async function createTicket(
  body: TicketCreateRequest,
  idempotencyKey: string = newIdempotencyKey(),
  signal?: AbortSignal,
): Promise<TicketDetail> {
  const { data } = await sdkCreateTicket({
    body,
    headers: { "Idempotency-Key": idempotencyKey },
    ...(signal === undefined ? {} : { signal }),
    throwOnError: true,
  });
  return data;
}

/**
 * Suggest a subject, a silent category and 0-3 follow-up questions for an
 * incident that has not been filed yet. Writes nothing.
 */
export async function getIncidentFollowups(
  body: IncidentFollowupsRequest,
): Promise<IncidentFollowupsResponse> {
  const { data } = await sdkGetIncidentFollowups({ body, throwOnError: true });
  return data;
}

/**
 * "Is this the same problem?" (8 Oct): open tickets about the same shared
 * system, scrubbed. Writes nothing; empty when the check is off or failing.
 */
export async function findSimilarTickets(
  body: SimilarTicketsRequest,
): Promise<SimilarTicketsResponse> {
  const { data } = await sdkFindSimilarTickets({ body, throwOnError: true });
  return data;
}

/** "Yes, add me to it": noted as affected on the open ticket; nothing new is raised. */
export async function joinSimilarTicket(
  body: SimilarTicketJoinRequest,
): Promise<SimilarTicketJoinResponse> {
  const { data } = await sdkJoinSimilarTicket({ body, throwOnError: true });
  return data;
}

export async function addTicketComment(
  ticketId: string,
  body: TicketCommentCreateRequest,
): Promise<TicketTimelineEntry> {
  const { data } = await sdkAddTicketComment({
    path: { ticket_id: ticketId },
    body,
    throwOnError: true,
  });
  return data;
}

export async function answerTicketInfoRequest(
  ticketId: string,
  infoRequestId: string,
  body: TicketInfoAnswerRequest,
): Promise<TicketDetail> {
  const { data } = await sdkAnswerTicketInfoRequest({
    path: { ticket_id: ticketId, info_request_id: infoRequestId },
    body,
    throwOnError: true,
  });
  return data;
}

/** The user-initiated close ("this is sorted"). Never a raw status change. */
export async function closeTicket(
  ticketId: string,
  body: TicketCloseRequest,
): Promise<TicketDetail> {
  const { data } = await sdkCloseTicket({
    path: { ticket_id: ticketId },
    body,
    throwOnError: true,
  });
  return data;
}

/** Accept the agent's proposed resolution (closes) or reject it (reopens). */
export async function confirmTicketResolution(
  ticketId: string,
  body: TicketResolutionConfirmRequest,
): Promise<TicketDetail> {
  const { data } = await sdkConfirmTicketResolution({
    path: { ticket_id: ticketId },
    body,
    throwOnError: true,
  });
  return data;
}

export async function reopenTicket(
  ticketId: string,
  body: TicketReopenRequest,
): Promise<TicketDetail> {
  const { data } = await sdkReopenTicket({
    path: { ticket_id: ticketId },
    body,
    throwOnError: true,
  });
  return data;
}
